import React, { useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import PlaidLinkToken from '../components/PlaidLinkToken';

function AccountsPage() {
  const [accounts, setAccounts] = useState([]);
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(true);
  const [isCreatingLinkToken, setIsCreatingLinkToken] = useState(false);
  const [isAddingAccount, setIsAddingAccount] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [plaidSession, setPlaidSession] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    getAccounts();

    // The account list is loaded only when the page first mounts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function authHeaders() {
    return {
      'Content-Type': 'application/json',
      'Authorization': 'Basic ' + Cookies.get('token')
    };
  }

  async function readResponse(response) {
    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      const message = data.error?.message || data.error || data.message;
      throw new Error(message || `Request failed (${response.status})`);
    }

    return data;
  }

  async function getAccounts(showLoading = true) {
    if (showLoading) {
      setIsLoadingAccounts(true);
    }

    try {
      const response = await fetch('/api/accounts', {
        method: 'GET',
        headers: authHeaders()
      });
      const data = await readResponse(response);
      setAccounts(data.accounts);
      return true;
    } catch (err) {
      console.log(err);
      setErrorMessage(err.message || 'Unable to load accounts.');
      return false;
    } finally {
      if (showLoading) {
        setIsLoadingAccounts(false);
      }
    }
  }

  async function addAccount() {
    setErrorMessage("");
    setSuccessMessage("");
    setIsCreatingLinkToken(true);

    try {
      const response = await fetch('/api/accounts/link-token', {
        method: 'POST',
        headers: authHeaders()
      });
      const data = await readResponse(response);
      setPlaidSession({ token: data.linkToken, mode: 'add' });
    } catch (err) {
      console.log(err);
      setErrorMessage(err.message || 'Unable to start Plaid Link.');
    } finally {
      setIsCreatingLinkToken(false);
    }
  }

  async function refreshAccounts() {
    setErrorMessage("");
    setSuccessMessage("");
    setIsRefreshing(true);

    try {
      const response = await fetch('/api/accounts/refresh', {
        method: 'POST',
        headers: authHeaders()
      });
      const data = await response.json().catch(() => ({}));

      if (data.link_token) {
        setPlaidSession({ token: data.link_token, mode: 'update' });
        return;
      }

      if (!response.ok) {
        const message = data.error?.message || data.error || data.message;
        throw new Error(message || `Refresh failed (${response.status})`);
      }

      setAccounts(data.accounts);
      setSuccessMessage('Accounts refreshed.');
    } catch (err) {
      console.log(err);
      setErrorMessage(err.message || 'Unable to refresh accounts.');
    } finally {
      setIsRefreshing(false);
    }
  }

  async function plaidSuccess(publicToken, metadata) {
    const session = plaidSession;
    setPlaidSession(null);

    if (session?.mode === 'update') {
      await refreshAccounts();
      return;
    }

    setErrorMessage("");
    setIsAddingAccount(true);

    try {
      const requestBody = { publicToken };
      const institutionName = metadata?.institution?.name;

      if (institutionName) {
        requestBody.institutionName = institutionName;
      }

      const response = await fetch('/api/accounts', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(requestBody)
      });
      const data = await readResponse(response);

      const accountsReloaded = await getAccounts(false);
      if (accountsReloaded) {
        setSuccessMessage(`${data.account.name} was connected.`);
      }
    } catch (err) {
      console.log(err);
      setErrorMessage(err.message || 'Unable to add this account.');
    } finally {
      setIsAddingAccount(false);
    }
  }

  function plaidExit(err) {
    setPlaidSession(null);

    if (err) {
      console.log(err);
      setErrorMessage(err.display_message || err.error_message || 'Plaid Link encountered an error. Please try again.');
    }
  }

  /*
   * A Plaid session is either for connecting a new institution or for
   * reauthenticating an existing one during refresh.
   */
  function renderPlaidLink() {
    if (!plaidSession) {
      return null;
    }

    return (
      <PlaidLinkToken
        plaidLinkToken={plaidSession.token}
        onPlaidSuccess={plaidSuccess}
        onPlaidExit={plaidExit}
      />
    );
  }

  function formatDateTime(date) {
    const dateObj = new Date(Date.parse(date));
    return dateObj.toLocaleString('default', { month: 'long', day: 'numeric', year: 'numeric', hour: 'numeric', minute: 'numeric' });
  }

  const isBusy = isCreatingLinkToken || isAddingAccount || isRefreshing || Boolean(plaidSession);

  return (
    <div className="col-lg-10 page-container">
      <h2>Accounts</h2>
      <div className="row">
        <div className="col">
          <div className="d-flex flex-wrap gap-2 mb-3">
            <button className="btn btn-primary" onClick={addAccount} disabled={isBusy}>
              {isCreatingLinkToken || isAddingAccount ? (
                <React.Fragment>
                  Connecting&nbsp;
                  <span className="spinner-border spinner-border-sm text-light" role="status">
                    <span className="visually-hidden">Connecting...</span>
                  </span>
                </React.Fragment>
              ) : '+ Add Account'}
            </button>
            <button className="btn btn-outline-primary" onClick={refreshAccounts} disabled={isBusy || accounts.length === 0}>
              {isRefreshing ? (
                <React.Fragment>
                  Refreshing&nbsp;
                  <span className="spinner-border spinner-border-sm" role="status">
                    <span className="visually-hidden">Refreshing...</span>
                  </span>
                </React.Fragment>
              ) : 'Refresh All'}
            </button>
          </div>
          {errorMessage && <div className="alert alert-danger" role="alert">{errorMessage}</div>}
          {successMessage && <div className="alert alert-success" role="status">{successMessage}</div>}
          {isLoadingAccounts && (
            <div className="d-flex align-items-center gap-2">
              <span className="spinner-border spinner-border-sm text-primary" role="status">
                <span className="visually-hidden">Loading accounts...</span>
              </span>
              Loading accounts...
            </div>
          )}
          {!isLoadingAccounts && accounts.length === 0 && (
            <p>No accounts are connected yet.</p>
          )}
          {!isLoadingAccounts && accounts.map((account) => (
            <div className="row account-row" key={account.id}>
              <div className="col">
                <div className="card">
                  <div className="card-body">
                    <h5 className="card-title">{account.name}</h5>
                    <p className="card-text">Last Updated: {formatDateTime(account.updatedAt)}</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {renderPlaidLink()}
    </div>
  );
}

export default AccountsPage;
